import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { change } from 'redux-form';
import get from 'lodash/get';
import useGontijoAccountDetails from 'hooks/useGontijoAccountDetails';
import {
  accountFieldsHeldBy,
  accountShortName,
  buildTravelAsAccountPatch,
} from 'utils/purchase/travelAsAccount';

/**
 * TravelAsAccountPill component. "Viajar como Maria G." — fills this passenger with the Gontijo
 * account the buyer signed in with.
 *
 * Offered on every passenger, not only the first: the account holder may be buying for someone else
 * and may well be passenger 2. The fields stay editable and the pill stays tappable. An account
 * travels once, so a tap also clears the account from any other passenger still holding it — a tap
 * on the wrong passenger is undone by tapping the right one, without leaving a duplicate behind.
 * Renders nothing until the carrier's record is in, and nothing at all without one.
 *
 * Mount it only where checkout login is on: it reads the session through `useGontijoSession`, which
 * needs the provider.
 * @param {object} props - Component props.
 * @param {string} props.passenger - The passenger's redux-form path, e.g. `passengers[1]`.
 * @param {boolean} [props.firstPassengerLocked] - Passenger 1 is copied from the purchaser ("the
 *   buyer is a passenger"), so a tap here must leave it alone even when it holds the account.
 * @returns {React.ReactElement|null} The pill.
 */
const TravelAsAccountPill = ({ passenger, firstPassengerLocked }) => {
  const { t } = useTranslation('passengers');
  const dispatch = useDispatch();
  // Read at tap time rather than subscribed to: every keystroke in any passenger would otherwise
  // re-render every pill.
  const store = useStore();
  const { account, email } = useGontijoAccountDetails();
  const documentTypePath = `form.passengers.values.${passenger}.documentType`;
  const selectedDocumentType = useSelector((state) => get(state, documentTypePath));

  const shortName = accountShortName(account?.name);
  if (!shortName) return null;

  /**
   * Clears the account from every other passenger still holding it, then writes it into this one,
   * one redux-form `change` per field.
   */
  const travelAsAccount = () => {
    const patch = buildTravelAsAccountPatch({ account, email, selectedDocumentType });
    const [, arrayName, ownIndex] = passenger.match(/^(.*)\[(\d+)\]$/) ?? [];
    const others = get(store.getState(), `form.passengers.values.${arrayName}`, []);

    others.forEach((values, index) => {
      if (index === Number(ownIndex) || (firstPassengerLocked && index === 0)) return;

      accountFieldsHeldBy(values, patch).forEach((field) => {
        dispatch(change('passengers', `${arrayName}[${index}].${field}`, ''));
      });
    });

    Object.entries(patch).forEach(([field, value]) => {
      dispatch(change('passengers', `${passenger}.${field}`, value));
    });
  };

  return (
    <button
      type="button"
      onClick={travelAsAccount}
      data-testid={`travel-as-account-pill-${passenger}`}
      // Shrinks rather than pushes: the heading beside it carries the seats, and a long name must
      // not shove them off the row.
      className="min-w-0 shrink truncate rounded-full bg-primary-regular px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-strong"
    >
      {t('travel_as_account', { name: shortName })}
    </button>
  );
};

TravelAsAccountPill.propTypes = {
  passenger: PropTypes.string.isRequired,
  firstPassengerLocked: PropTypes.bool,
};

TravelAsAccountPill.defaultProps = {
  firstPassengerLocked: false,
};

export default TravelAsAccountPill;
